// Draft conversion follows the server's update_validation.py and set_views.py.
const INT32_MIN = -(2 ** 31);
const INT32_MAX = 2 ** 31 - 1;

function integerDraft(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && /^[+-]?\d+$/.test(value.trim()))
    return Number(value.trim());
  return NaN;
}

export function nullableInt32(value, field = "BPM") {
  if (value === null || (typeof value === "string" && !value.trim()))
    return null;
  const number = integerDraft(value);
  if (!Number.isInteger(number) || number < INT32_MIN || number > INT32_MAX)
    throw new Error(
      `${field} must be a whole number from ${INT32_MIN} to ${INT32_MAX}, or blank.`,
    );
  return number;
}

export function requestId(value, field = "Track ID") {
  const number = integerDraft(value);
  if (!Number.isSafeInteger(number) || number <= 0)
    throw new Error(`${field} must be a positive integer.`);
  return number;
}

function validateText(payload, limits) {
  for (const [field, limit] of Object.entries(limits)) {
    if (!Object.hasOwn(payload, field)) continue;
    const value = payload[field];
    if (typeof value !== "string") throw new Error(`${field} must be text.`);
    if (limit !== null && Array.from(value).length > limit)
      throw new Error(`${field} must contain at most ${limit} characters.`);
  }
}

function numericTrackFields(fields) {
  const payload = { ...fields };
  if (Object.hasOwn(payload, "bpm")) payload.bpm = nullableInt32(payload.bpm);
  validateText(payload, { notes: null, duration: 20 });
  return payload;
}

/** @typedef {{bpm?: number|null, duration?: string, notes?: string, quotes?: string, position?: string, title?: string, like?: boolean, love?: boolean, hate?: boolean, is_instrumental?: boolean, is_a_cappella?: boolean, tags?: string|string[]|null, breaks?: string[]|null, featured_artists?: string[]|null}} TrackUpdateDto */

/** @returns {TrackUpdateDto} */
export function trackUpdatePayload(fields) {
  const payload = numericTrackFields(fields);
  validateText(payload, { quotes: null, position: 10, title: 512 });
  for (const field of [
    "like",
    "love",
    "hate",
    "is_instrumental",
    "is_a_cappella",
  ]) {
    if (Object.hasOwn(payload, field) && typeof payload[field] !== "boolean")
      throw new Error(`${field} must be a boolean.`);
  }
  for (const [field, limit] of Object.entries({
    tags: 255,
    breaks: 100,
    featured_artists: 255,
  })) {
    if (!Object.hasOwn(payload, field) || payload[field] === null) continue;
    const value = payload[field];
    const names =
      field === "tags" && typeof value === "string"
        ? value
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean)
        : value;
    if (
      !Array.isArray(names) ||
      names.some(
        (name) => typeof name !== "string" || Array.from(name).length > limit,
      )
    )
      throw new Error(
        `${field} must contain names of at most ${limit} characters.`,
      );
  }
  return payload;
}

/** @typedef {{bpm?: number|null, duration?: string, notes?: string, position?: number|null}} SetTrackUpdateDto */

/** @returns {SetTrackUpdateDto} */
export function setTrackUpdatePayload(fields) {
  const payload = numericTrackFields(fields);
  if (Object.hasOwn(payload, "position"))
    payload.position = nullableInt32(payload.position, "Set position");
  return payload;
}
